/**
 * @file error-mixin.ts
 *
 * Owns error capture, normalization, and server-side logging. Registers
 * global window 'error' and 'unhandledrejection' handlers that surface errors
 * through the modal overlay and log them to the server. Also sends an
 * 'app_closed' beacon on 'beforeunload'.
 *
 * sendLog suppresses all server calls in localDev / iframe / noAPI
 * environments; in localDev with debug-log-notifications it emits a visible
 * as-notify event instead.
 *
 * Requires: ModalMixinMembers (for showErrorModal)
 * Exposes: logError, sendLog, errorMessage
 */
import { sendLogToServer, sendBeaconToServer } from '../../../logic/asApi.ts';
import { localDev, inIframe, noAPI, currentEnv } from '../../../data/constants.ts';
import { devlog } from '../../../logic/utils.ts';
import { type Constructor } from './mixin-types-utils.ts';
import type { ModalMixinMembers } from './modal-mixin.ts';
import type { PhpApiError } from '../../../data/data-interfaces.external.ts';
import { currentProjectData } from '../../../data/projectManagement.ts';
import { liveAppData } from '../../../store/store.ts';

/** Surface added by ErrorMixin. Referenced by ProjectMixin and MeshMixin for
 *  structured error reporting and server-side event logging. */
export interface ErrorMixinMembers {
  logError(origin: string, err: unknown, importance?: 'critical' | 'regular' | 'warning', label?: string): void;
  /** Second arg may be a subcategory string (then `data` is the payload) or the payload object itself. */
  sendLog(evName: string, subcategoryOrData?: string | object, data?: object): void;
  errorMessage(err: unknown): string;
}

export const ErrorMixin = <T extends Constructor<HTMLElement & ModalMixinMembers>>(Base: T) =>
  class ErrorMixin extends Base {
    private _isAbortError(err: unknown): boolean {
      if (err instanceof Error) {
        if (err.name === 'AbortError') return true;
        if (/\bstatus 499\b/.test(err.message)) return true;
        if (/"status"\s*:\s*499\b/.test(err.message)) return true;
      }
      if (typeof err === 'string') {
        if (/\bstatus 499\b/.test(err)) return true;
        if (/"status"\s*:\s*499\b/.test(err)) return true;
      }
      // Plain object from PHP/Paramate SDK: PhpApiError shape
      if (err !== null && typeof err === 'object') {
        const e = err as PhpApiError;
        if (e.status === 499) return true;
        if (typeof e.response?.message === 'string' && e.response.message.toLowerCase().includes('aborted')) return true;
      }
      return false;
    }

    private _notifyAborted(): void {
      this.dispatchEvent(new CustomEvent('as-notify', {
        detail: {
          defaultText: "Operation aborted - if you didn't cancel maybe your connection is unstable.",
          type: 'warning',
        },
      }));
    }

    private _handleWindowError = (event: ErrorEvent): void => {
      if (event.message?.includes('ResizeObserver loop completed with undelivered notifications')) return;
      if (this._isAbortError(event.error) || /\bstatus 499\b/.test(event.message ?? '')) {
        this._notifyAborted();
        return;
      }
      devlog('Got error', event);
      this.sendLog('error', {
        origin: 'uncaught',
        message: event.message ?? this.errorMessage(event.error),
        file: event.filename,
        line: event.lineno,
        col: event.colno,
        importance: 'critical',
      });
      this.showErrorModal({ error: event.error, origin: 'uncaught' });
    };

    private _handleUnhandledRejection = (event: PromiseRejectionEvent): void => {
      const reason = event.reason;
      if (this._isAbortError(reason)) {
        this._notifyAborted();
        return;
      }
      const message = this.errorMessage(reason);
      devlog('Unhandled promise rejection', reason);
      this.sendLog('error', { origin: 'unhandled-rejection', message, stack: reason?.stack ?? '', importance: 'critical' });
      this.showErrorModal({ error: reason instanceof Error ? reason : new Error(message), origin: 'unhandled-rejection' });
    };

    private _handleAsLogError = (event: Event): void => {
      const { origin, error, importance = 'regular', notify } = (event as CustomEvent).detail ?? {};
      this.logError(origin ?? 'unknown', error, importance);
      if (notify) {
        this.dispatchEvent(new CustomEvent('as-notify', {
          detail: { defaultText: notify, type: 'error' },
        }));
      }
    };

    private _handleBeforeUnload = (): void => {
      if (localDev || currentEnv === 'onprem' || inIframe || noAPI) return;
      sendBeaconToServer(liveAppData.appId === 'storefront' ? 'storefront_closed' : 'app_closed');
    };

    async connectedCallback() {
      await super.connectedCallback?.();
      window.addEventListener('error', this._handleWindowError as EventListener);
      window.addEventListener('unhandledrejection', this._handleUnhandledRejection as EventListener);
      window.addEventListener('beforeunload', this._handleBeforeUnload as EventListener);
      window.addEventListener('as-log-error', this._handleAsLogError as EventListener);
    }

    disconnectedCallback() {
      window.removeEventListener('error', this._handleWindowError as EventListener);
      window.removeEventListener('unhandledrejection', this._handleUnhandledRejection as EventListener);
      window.removeEventListener('beforeunload', this._handleBeforeUnload as EventListener);
      window.removeEventListener('as-log-error', this._handleAsLogError as EventListener);
      super.disconnectedCallback?.();
    }

    errorMessage(err: unknown): string {
      if (err instanceof Error) return err.message;
      if (typeof err === 'string') return err;
      if (err === undefined || err === null) return String(err);
      try { return JSON.stringify(err) ?? String(err); } catch { return String(err); }
    }

    public logError(origin: string, err: unknown, importance: 'critical' | 'regular' | 'warning' = 'regular', label?: string): void {
      console.error(label ?? `[AS-APP] ${origin}:`, err);
      this.sendLog('error', { origin, message: this.errorMessage(err), importance });
    }

    sendLog(evName: string, subcategoryOrData?: string | object, data?: object) {
      // Backward-compatible signature: the second arg is an optional subcategory
      // string (e.g. 'local' | 'interapp' | 'grabcad' for data-import/data-export),
      // in which case `data` carries the payload. If it's an object, it IS the
      // payload and there is no subcategory (legacy two-arg form).
      const subcategory = typeof subcategoryOrData === 'string' ? subcategoryOrData : undefined;
      const body = (typeof subcategoryOrData === 'string' ? data : subcategoryOrData) as object | undefined;
      const sessionId = currentProjectData.sessionId || undefined;
      const payload = sessionId
        ? { sessionId, ...body }
        : body;
      if (localDev || currentEnv === 'onprem' || inIframe || noAPI) {
        if (!inIframe && !noAPI && this.hasAttribute('debug-log-notifications')) {
          const sub = subcategory ? `/${subcategory}` : '';
          const details = payload ? ` | ${JSON.stringify(payload)}` : '';
          this.dispatchEvent(new CustomEvent('as-notify', {
            detail: { defaultText: `Log event (not sent): ${evName}${sub}${details}`, type: 'warning' },
          }));
        }
        return;
      }
      return sendLogToServer(evName, payload, subcategory).catch(console.error);
    }
  };
